import React, { ReactNode } from "react";
import { TestID, TestIDWrapper } from "../../shared/testids/testids";
import { COREBody, COREBodyProps } from "../Typography/COREBody";
import { Col, Row, TooltipProps } from "antd";
import { CORETooltip, CORETooltipProps } from "../Overlay/CORETooltip";
import { COREIcon } from "./COREIcon";
import { getColor } from "./COREColour";
import classNames from "classnames";
import { CurrencySymbol } from "../../shared/CurrencySymbol";
import { ArrowRightOutlined } from "@ant-design/icons";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./COREStats.less";
import { spacing } from "../../shared/global";
import { ContentLockedTooltip } from "../../shared/tooltips/ContentLockedTooltip";
import { UpgradeType } from "../../shared/modals/SubscriptionUpgradeModal";

type Color = string | number;

export type EmissionUnit = {
  unit: "tCO₂e" | "kgCO₂e";
  value: number | null | undefined;
};
export type StatsSize = "sm" | "md" | "lg";
type SpacingSize = "none" | "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
export type MagnitudeEmission = "" | "k" | "M" | "G" | "auto";
export type MagnitudeCurrency =
  | "2Decimal"
  | "noDecimal"
  | "k"
  | "m"
  | "b"
  | "auto";
export type MagnitudeNumber = "" | "K" | "M" | "B" | "T" | "auto";
export type Magnitude = MagnitudeEmission | MagnitudeCurrency | MagnitudeNumber;

type StatsProps = {
  title?: React.ReactNode;
  titleSize?: COREBodyProps["type"];
  unit?: React.ReactNode;
  defaultUnit?: React.ReactNode;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  size?: StatsSize;
  spacingSize?: SpacingSize;
  className?: string;
  color?: Color;
  testID: TestID;
  titleTooltip?: React.ReactNode;
  messageTooltip?: React.ReactNode;
  triggerTooltip?: TooltipProps["trigger"];
  currencySymbol?: CurrencySymbol;
  positiveSymbol?: boolean;
  footnote?: COREBodyProps["children"];
  disabled?: boolean;
  tooltipHidden?: CORETooltipProps["hidden"];
  displayUnit?: boolean;
  decimalPlaces?: number;
  // Present means the value is withheld by subscription scope. A locked stat
  // replaces its value with a lock whose only affordance is the upgrade
  // tooltip, so the upgrade type it needs lives inside the same object and
  // cannot be forgotten. Two independent optional props allowed a lock with no
  // way to upgrade; a discriminated union expressed that too, but multiplied
  // COREStatsType and broke every `Pick<COREStatsData, ...>` consumer.
  contentLock?: { upgradeType: UpgradeType };
};

type StatBodyProps = Pick<
  COREStatsProps,
  "iconLeft" | "iconRight" | "testID" | "size" | "children"
> & {
  heading: ReactNode;
  fromValue: ReactNode;
};

export type COREStatsType =
  | {
      type?: "emission";
      magnitude?: MagnitudeEmission;
    }
  | {
      type?: "currency";
      magnitude?: MagnitudeCurrency;
    }
  | {
      type: "ratio";
      magnitude?: never;
    }
  | {
      type: "number";
      magnitude?: MagnitudeNumber;
    }
  | {
      type: "default";
      magnitude?: never;
    };

type RenderTooltipMessage = (
  fromValue?: number | null,
  value?: number | null,
  unit?: ReactNode
) => ReactNode;

export type COREStatsWithoutChildren = {
  fromValue?: number | null;
  value?: number | null;
  defaultValue?: number | null;
  renderTooltipMessage?: RenderTooltipMessage;
  children?: never;
} & COREStatsType &
  StatsProps;

type COREStatsWithChildren = {
  value?: never;
  defaultValue?: never;
  renderTooltipMessage?: RenderTooltipMessage;
  children?: React.ReactNode;
  type?: never;
  fromValue?: never;
  magnitude?: never;
} & StatsProps;

export type COREStatsProps = COREStatsWithoutChildren | COREStatsWithChildren;
export type ConvertStatsValue = Pick<
  COREStatsProps,
  | "type"
  | "unit"
  | "defaultUnit"
  | "currencySymbol"
  | "positiveSymbol"
  | "value"
  | "defaultValue"
  | "decimalPlaces"
> & {
  type: Exclude<COREStatsProps["type"], undefined>;
  magnitude: Exclude<COREStatsProps["magnitude"], undefined>;
};

const format2DigitDecimal = (value: number) =>
  (Math.round(value * 100) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatNoDecimal = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

export const getEmissionUnitPerGroup = (
  currentValue: number | null | undefined,
  maxValue: number
): EmissionUnit => {
  // unit of the default value is tonnes
  const displayKilogram = maxValue !== 0 && maxValue < 10;
  const unit: EmissionUnit["unit"] = displayKilogram ? "kgCO₂e" : "tCO₂e";
  const value =
    displayKilogram && currentValue ? currentValue * 1000 : currentValue;

  return { unit: unit, value: value };
};

export const getMagnitudeEmissionByThresholds = (
  value: number
): MagnitudeEmission => {
  switch (true) {
    case value < 0:
      return getMagnitudeEmissionByThresholds(-value);
    case value >= 10_000_000_000:
      return "G";
    case value >= 10_000_000:
      return "M";
    case value >= 10_000:
      return "k";
    default:
      return "";
  }
};

export const getMagnitudeCurrencyByThresholds = (
  value: number
): MagnitudeCurrency => {
  switch (true) {
    case value < 0:
      return getMagnitudeCurrencyByThresholds(-value);
    case value >= 1_000_000_000:
      return "b";
    case value >= 1_000_000:
      return "m";
    case value >= 100_000:
      return "k";
    case value >= 1000:
      return "noDecimal";
    default:
      return "2Decimal";
  }
};

export const getMagnitudeByThresholds = (
  type: COREStatsType["type"],
  value: number
) => {
  if (type === "currency") {
    return getMagnitudeCurrencyByThresholds(value);
  } else if (type === "number") {
    return findMagnitudeNumberUnit(value);
  } else {
    return getMagnitudeEmissionByThresholds(value);
  }
};

export function formatNumberWithMaxDecimals(number: number) {
  return new Intl.NumberFormat("en-US", {}).format(number);
}

export const formatCurrency = (
  value: number,
  magnitude: MagnitudeCurrency
): string => {
  switch (magnitude) {
    case "b":
      return `${format2DigitDecimal(value / 1_000_000_000)}b`;
    case "m":
      return `${format2DigitDecimal(value / 1_000_000)}m`;
    case "k":
      return `${formatNoDecimal(value / 1000)}k`;
    case "noDecimal":
      return `${new Intl.NumberFormat("en-US", {
        style: "decimal",
        maximumFractionDigits: 0,
      }).format(value)}`;
    default:
      return `${format2DigitDecimal(value)}`;
  }
};

export const formatEmission = (
  value: number,
  magnitude: MagnitudeEmission,
  decimalPlaces?: number
): string => {
  const dp = decimalPlaces ?? 2;
  const formatWithDecimals = (v: number) =>
    v.toLocaleString(undefined, {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    });
  switch (magnitude) {
    case "G":
      return formatWithDecimals(value / 1_000_000_000);
    case "M":
      return formatWithDecimals(value / 1_000_000);
    case "k":
      return formatWithDecimals(value / 1000);
    default:
      return formatWithDecimals(value);
  }
};

export const formatEmissionUnit = (
  unit: string,
  magnitude: MagnitudeEmission
) => `${magnitude}${unit ?? ""}`;

export const formatRatio = (
  value: COREStatsProps["fromValue"] | COREStatsProps["value"],
  positiveSymbol?: boolean
) => {
  if (value === null) return "-";
  if (value === undefined) return undefined;
  return `${positiveSymbol ? "+" : ""}${format2DigitDecimal(value * 100)}`;
};

const displayEmissionFormat = (
  value: COREStatsProps["fromValue"] | COREStatsProps["value"],
  magnitude: MagnitudeEmission,
  positiveSymbol?: boolean,
  decimalPlaces?: number
) => {
  if (value === null) return "-";
  if (value === undefined) return undefined;
  return `${positiveSymbol ? "+" : ""}${formatEmission(
    value,
    magnitude,
    decimalPlaces
  )}`;
};

const findMagnitudeNumberUnit = (num: number): MagnitudeNumber => {
  const units: MagnitudeNumber[] = ["", "K", "M", "B", "T"];
  if (num === 0) return units[num];
  const groupEveryThreeDigits = 3;
  const magnitude = Math.floor(
    Math.log10(Math.abs(num)) / groupEveryThreeDigits
  );
  return units[Math.min(units.length - 1, magnitude)];
};

const formatNumeric = (
  value: number,
  unit: MagnitudeNumber,
  decimalPlaces?: number
) => {
  const groupEveryThreeDigits = 3;
  const normalizedValue =
    value /
    Math.pow(
      10,
      ["", "K", "M", "B", "T"].indexOf(unit) * groupEveryThreeDigits
    );
  const hasUnitPrefix = unit !== "";
  const resolvedDecimals = decimalPlaces ?? (hasUnitPrefix ? 2 : undefined);
  return `${new Intl.NumberFormat(undefined, {
    ...(resolvedDecimals !== undefined && {
      minimumFractionDigits: 0,
      maximumFractionDigits: resolvedDecimals,
    }),
  }).format(normalizedValue)}${unit}`;
};

export const statFormat = (
  value: number,
  type: COREStatsType["type"],
  magnitude: Magnitude,
  unit?: StatsProps["unit"],
  positiveSymbol?: boolean,
  currencySymbol?: CurrencySymbol,
  decimalPlaces?: number
) => {
  const isPositive = value > 0 && positiveSymbol;
  if (type === "default") {
    return {
      format: value.toLocaleString(undefined, {
        minimumFractionDigits: decimalPlaces ?? 0,
        maximumFractionDigits: decimalPlaces ?? 0,
      }),
    };
  }

  if (type === "number") {
    return {
      format: formatNumeric(value, magnitude as MagnitudeNumber, decimalPlaces),
      tooltipFormat: value.toLocaleString(),
    };
  }

  if (type === "ratio") {
    return {
      format: formatRatio(value, isPositive),
    };
  }

  if (type === "currency") {
    const formattedCurrency = formatCurrency(
      Math.abs(value),
      magnitude as MagnitudeCurrency
    );
    const sign = value < 0 ? "-" : "";
    return {
      format: `${sign}${currencySymbol}${formattedCurrency}`,
    };
  }

  return {
    format: displayEmissionFormat(
      value,
      magnitude as MagnitudeEmission,
      isPositive,
      decimalPlaces
    ),
    unitFormat: formatEmissionUnit(
      unit as string,
      magnitude as MagnitudeEmission
    ),
  };
};

export const formatterStatsValue = ({
  magnitude,
  type,
  value,
  currencySymbol,
  positiveSymbol,
  decimalPlaces,
}: ConvertStatsValue) => {
  if (value === undefined || value === null) return value;

  const isPositive = value > 0 && positiveSymbol;
  const magnitudeValue: Magnitude =
    magnitude === "auto" ? getMagnitudeByThresholds(type, value) : magnitude;
  switch (type) {
    case "default":
      return value.toLocaleString(undefined, {
        minimumFractionDigits: decimalPlaces ?? 0,
        maximumFractionDigits: decimalPlaces ?? 0,
      });
    case "number":
      return formatNumeric(
        value,
        magnitudeValue as MagnitudeNumber,
        decimalPlaces
      );
    case "ratio":
      return formatRatio(value, isPositive);
    case "currency": {
      const formattedCurrency = formatCurrency(
        Math.abs(value),
        magnitudeValue as MagnitudeCurrency
      );
      const sign = value < 0 ? "-" : "";
      return `${sign}${currencySymbol}${formattedCurrency}`;
    }
    default:
      return displayEmissionFormat(
        value,
        magnitudeValue as MagnitudeEmission,
        isPositive,
        decimalPlaces
      );
  }
};

const formatterUnitStatsValue = ({
  magnitude,
  type,
  value,
  unit,
}: ConvertStatsValue) => {
  if (type !== "emission" || !value) return unit;

  const magnitudeValue: Magnitude =
    magnitude === "auto" ? getMagnitudeByThresholds(type, value) : magnitude;
  return formatEmissionUnit(
    unit as string,
    magnitudeValue as MagnitudeEmission
  );
};

export const formatterTooltipStatValue = ({
  value,
  type,
  unit,
  decimalPlaces,
}: {
  value: ConvertStatsValue["value"];
  type: ConvertStatsValue["type"];
  unit: ConvertStatsValue["unit"];
  decimalPlaces: ConvertStatsValue["decimalPlaces"];
}) => {
  if (value === undefined || value === null) return "-";

  switch (type) {
    case "default":
      return `${value.toLocaleString(undefined, {
        minimumFractionDigits: decimalPlaces ?? 0,
        maximumFractionDigits: decimalPlaces ?? 0,
      })} ${unit ?? ""}`.trim();
    case "number":
      return value.toLocaleString();
    case "ratio":
      return `${formatNumberWithMaxDecimals(value * 100)} ${unit}`;
    case "currency":
      return `${formatNumberWithMaxDecimals(value)} ${unit}`;
    default:
      return `${formatNumberWithMaxDecimals(value)} ${unit}`;
  }
};

const IconLeftAndRight: React.FC<
  Pick<StatBodyProps, "iconRight" | "iconLeft" | "testID" | "children"> & {
    className?: string;
  }
> = ({ iconLeft, iconRight, testID, children, className }) => (
  <Row
    justify={"center"}
    align={"middle"}
    gutter={[spacing.xs, 0]}
    className={className}
  >
    {iconLeft && (
      <Col>
        <TestIDWrapper testID={`${testID}-icon-left`}>
          <div className={"stats-icon-left"}>{iconLeft}</div>
        </TestIDWrapper>
      </Col>
    )}
    {children}
    {iconRight && (
      <Col>
        <TestIDWrapper testID={(testID + "-icon-right") as TestID}>
          <div className={"stats-icon-right"}>{iconRight}</div>
        </TestIDWrapper>
      </Col>
    )}
  </Row>
);

const StatBody: React.FC<StatBodyProps> = ({
  iconLeft,
  iconRight,
  testID,
  size = "md",
  heading,
  fromValue,
}) => {
  if (fromValue)
    return (
      <Row justify={"center"} align={"middle"} gutter={[spacing.xs, 0]}>
        <IconLeftAndRight
          iconLeft={iconLeft}
          testID={testID}
          iconRight={iconRight}
          className={"fromValue"}
        >
          <Col>
            <div className={classNames(`stats-${size}`, "from-heading")}>
              {fromValue}
            </div>
          </Col>
        </IconLeftAndRight>
        <Col>
          <ArrowRightOutlined
            className={`arrow-${size}`}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        </Col>
        <IconLeftAndRight
          iconLeft={iconLeft}
          testID={testID}
          iconRight={iconRight}
        >
          <Col>
            <div className={`stats-${size}`}>{heading}</div>
          </Col>
        </IconLeftAndRight>
      </Row>
    );

  return (
    <IconLeftAndRight iconLeft={iconLeft} testID={testID} iconRight={iconRight}>
      <Col>
        <div className={`stats-${size}`}>{heading ?? "-"}</div>
      </Col>
    </IconLeftAndRight>
  );
};

const heading = (
  color: COREStatsProps["color"],
  disabled: COREStatsProps["disabled"],
  child: COREStatsProps["children"]
) => {
  if (child === undefined || child === null)
    return <span style={{ color: `var(--grey-140)` }}>{"-"}</span>;
  if (color && !disabled) {
    return <span style={{ color: `var(--${color})` }}>{child}</span>;
  }
  return child;
};

export const COREStats: React.FC<COREStatsProps> = ({
  title,
  titleSize = "p2",
  unit,
  defaultUnit = unit,
  iconLeft,
  iconRight,
  size = "md",
  spacingSize = "sm",
  type = "emission",
  className,
  color,
  titleTooltip,
  messageTooltip,
  triggerTooltip,
  magnitude = "auto",
  children,
  testID,
  value,
  defaultValue = value,
  currencySymbol = "$",
  positiveSymbol = false,
  footnote,
  fromValue,
  disabled = false,
  tooltipHidden,
  renderTooltipMessage,
  displayUnit = true,
  decimalPlaces,
  contentLock,
}) => {
  const childElement: React.ReactNode =
    value || value === 0
      ? formatterStatsValue({
          magnitude,
          type,
          value,
          currencySymbol,
          positiveSymbol,
          decimalPlaces,
        })
      : children;
  const unitElement = formatterUnitStatsValue({ magnitude, type, value, unit });
  const messageTooltipsGenerated = formatterTooltipStatValue({
    value: defaultValue,
    type,
    unit: defaultUnit,
    decimalPlaces,
  });
  const fromElement = formatterStatsValue({
    magnitude,
    type,
    value: fromValue,
    currencySymbol,
    positiveSymbol,
    decimalPlaces,
  });
  const lockedBody = (
    <Row justify={"center"} align={"middle"} gutter={[spacing.xs, 0]}>
      <Col>
        <TestIDWrapper testID={`${testID}/content-lock`}>
          <div className={`stats-${size}`}>
            <COREIcon
              color={getColor("grey100")}
              icon={icon({ name: "lock", style: "solid" })}
            />
          </div>
        </TestIDWrapper>
      </Col>
    </Row>
  );

  return (
    <TestIDWrapper className="core-stats-container" testID={testID}>
      <div
        className={classNames([
          "core-stats",
          size,
          className,
          { disabled: disabled },
          `inner-spacing-${spacingSize}`,
        ])}
      >
        <div className={"stats-title"}>
          <COREBody testID={`${testID}-title`} type={titleSize} strong>
            {title}
          </COREBody>
        </div>
        <div>
          {contentLock ? (
            // The value is withheld from the API for this user, so the lock
            // replaces the value tooltip entirely rather than nesting inside
            // it — otherwise the empty value tooltip renders over the upgrade
            // call to action.
            <ContentLockedTooltip
              testID={`${testID}/content-locked-tooltip`}
              upgradeType={contentLock.upgradeType}
              displayTooltip={true}
              noFitContent
            >
              {lockedBody}
            </ContentLockedTooltip>
          ) : (
            <CORETooltip
              {...(triggerTooltip && { trigger: triggerTooltip })}
              title={titleTooltip || title}
              message={
                renderTooltipMessage ? (
                  renderTooltipMessage(fromValue, value, unit)
                ) : (
                  <div className={"text-center"}>
                    {typeof fromValue === "number" ? (
                      <div>
                        {fromValue.toLocaleString()} {unit}
                      </div>
                    ) : null}
                    <div>
                      {messageTooltip ||
                        messageTooltipsGenerated ||
                        childElement ||
                        "-"}
                    </div>
                  </div>
                )
              }
              position={"top"}
              width={"auto"}
              testID={`${testID}-test-tooltips`}
              hidden={tooltipHidden}
              noFitContent
            >
              <StatBody
                testID={testID}
                heading={heading(color, disabled, childElement)}
                iconLeft={iconLeft}
                iconRight={iconRight}
                size={size}
                fromValue={fromElement}
              />
            </CORETooltip>
          )}
        </div>
        <div className={"stats-unit"}>
          {displayUnit && (
            <COREBody testID={`${testID}-unit`} type={"p4"} strong>
              {unitElement}
            </COREBody>
          )}
          {footnote && (
            <COREBody testID={`${testID}-footnote`} type={"p5"} strong>
              {footnote}
            </COREBody>
          )}
        </div>
      </div>
    </TestIDWrapper>
  );
};
